import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CalendarDays, Clock, Sparkles, ArrowLeft, BookOpen, Flame, Info, ArrowRight } from "lucide-react";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

export default function PanchangDetail() {
  const { kind, id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [muhurat, setMuhurat] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const path = kind === "ritual" ? `/vedic-rituals/${id}` : `/festivals/${id}`;
    api.get(path).then((r) => setItem(r.data)).catch(() => setNotFound(true));
  }, [kind, id]);

  useEffect(() => {
    if (!item?.date) return;
    api.get("/panchang/muhurat", { params: { date: String(item.date).slice(0, 10) } }).then((r) => setMuhurat(r.data)).catch(() => {});
  }, [item]);

  if (notFound) {
    return (
      <div className="px-4 max-w-3xl mx-auto py-24 text-center" data-testid="panchang-detail-notfound">
        <h1 className="font-serif text-2xl font-bold">Not found</h1>
        <p className="text-muted-foreground mt-2">This entry could not be located.</p>
        <Link to="/vedic-services/panchang"><Button className="mt-6 rounded-full" data-testid="back-to-panchang-btn">Back to Panchang</Button></Link>
      </div>
    );
  }
  if (!item) return <div className="py-32 text-center text-muted-foreground" data-testid="panchang-detail-loading">Loading…</div>;

  const methodLabel = kind === "ritual" ? "Ritual method" : "Celebration method";
  const bestCho = muhurat?.choghadiya_day?.find((c) => c.type !== "Inauspicious");

  return (
    <div data-testid="panchang-detail-page">
      {/* Hero */}
      <div className="relative h-[46vh] min-h-[320px] w-full overflow-hidden">
        <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        <div className="absolute inset-0 flex flex-col justify-end px-4 sm:px-6 lg:px-12 pb-8 max-w-7xl mx-auto w-full">
          <button
            onClick={() => navigate("/vedic-services/panchang")}
            data-testid="detail-back-btn"
            className="inline-flex items-center gap-1.5 text-sm text-white/90 hover:text-white mb-4 w-fit">
            <ArrowLeft className="h-4 w-4" /> Back to Panchang
          </button>
          <span className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-accent/90 text-white w-fit mb-3">{item.type}</span>
          <h1 className="font-serif text-3xl lg:text-5xl font-bold text-white leading-tight">{item.name}</h1>
          <p className="flex items-center gap-1.5 text-white/90 mt-3 text-sm">
            <CalendarDays className="h-4 w-4 text-accent" /> {fmtDate(item.date)}
          </p>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-12 lg:py-16 grid lg:grid-cols-3 gap-10">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-10">
          <section data-testid="detail-about">
            <h2 className="font-serif text-2xl font-semibold mb-3 flex items-center gap-2"><BookOpen className="h-5 w-5 text-accent" /> About {item.name}</h2>
            <p className="text-muted-foreground leading-relaxed">{item.brief}</p>
            <h3 className="font-serif text-lg font-semibold mt-6 mb-2">Religious &amp; spiritual significance</h3>
            <p className="text-muted-foreground leading-relaxed">{item.significance}</p>
          </section>

          <section data-testid="detail-method">
            <h2 className="font-serif text-2xl font-semibold mb-3 flex items-center gap-2"><Flame className="h-5 w-5 text-accent" /> {methodLabel}</h2>
            <p className="text-muted-foreground leading-relaxed">{item.method}</p>
          </section>

          <section data-testid="detail-other">
            <h2 className="font-serif text-2xl font-semibold mb-3 flex items-center gap-2"><Info className="h-5 w-5 text-accent" /> Other information</h2>
            <p className="text-muted-foreground leading-relaxed">{item.other_info}</p>
          </section>
        </div>

        {/* Sidebar: timings */}
        <aside className="space-y-5">
          <div className="rounded-2xl border border-border bg-card p-6 space-y-3 sticky top-24" data-testid="detail-timings">
            <h3 className="font-serif text-lg font-semibold flex items-center gap-2"><Clock className="h-4 w-4 text-accent" /> Tithi Timings</h3>
            <p className="text-sm text-muted-foreground">{item.tithi}</p>

            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 mt-2">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-2"><Sparkles className="h-3.5 w-3.5" /> Auspicious Timings</p>
              <div className="flex justify-between text-sm py-1"><span className="text-muted-foreground">Start time</span><span className="font-medium">{item.aus_start}</span></div>
              <div className="flex justify-between text-sm py-1"><span className="text-muted-foreground">End time</span><span className="font-medium">{item.aus_end}</span></div>
            </div>

            {kind === "ritual" && item.service_id && (
              <Link to={`/book/ritual?service=${item.service_id}`} data-testid="detail-book-btn" className="block pt-1">
                <Button className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
                  Book this Ritual <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </Link>
            )}
          </div>

          {muhurat && muhurat.source === "prokerala" && (muhurat.auspicious.length > 0 || bestCho) && (
            <div className="rounded-2xl border border-accent/40 bg-card p-6 space-y-1" data-testid="detail-puja-muhurat">
              <h3 className="font-serif text-lg font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-accent" /> Best Puja Muhurat</h3>
              <p className="text-xs text-muted-foreground pb-2">Live auspicious windows for {fmtDate(item.date)} · Varanasi.</p>
              {muhurat.auspicious.map((a) => (
                <div key={a.name} className="flex justify-between text-sm py-1 border-b border-border/40"><span className="text-muted-foreground">{a.name}</span><span className="font-medium">{a.time}</span></div>
              ))}
              {bestCho && (
                <div className="flex justify-between text-sm py-1"><span className="text-muted-foreground">Best Choghadiya ({bestCho.name})</span><span className="font-medium">{bestCho.time}</span></div>
              )}
              <p className="text-[11px] text-muted-foreground pt-2">Timings are computed live for the festival date. For a personalised muhurat, consult our purohits.</p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
